import React, { useState } from 'react';
import { Button, CircularProgress, Alert, Snackbar } from '@mui/material';
import { Save } from '@mui/icons-material';
import { useNavigate, NavigateFunction } from 'react-router-dom';
import { useMsal, useAccount } from '@azure/msal-react';
import { useAppSelector } from '../../../../../app/hooks';
import { useUpsertComplianceItemMutation } from '../../../../../services/ComplianceService/complianceApi';
import { ComplianceItemView } from '../../../../../services/ComplianceService/types/complianceTypes';

interface SaveComplianceItemButtonProps {
  label?: string;
  successMessage?: string;
}

// One validator per form section, each returning the labels of its own missing required fields.
// Keeping these separate from validateRequiredFields below is what keeps that function's
// complexity low as more sections/fields are added.
const validateIdentificationFields = (item: ComplianceItemView): string[] => {
  const missing: string[] = [];
  if (!item.title?.trim()) missing.push('Title');
  if (!item.complianceType?.trim()) missing.push('Compliance Type');
  if (!item.accreditationBody?.trim()) missing.push('Accreditation Body');
  return missing;
};

const validateFacilityFields = (item: ComplianceItemView): string[] => {
  const missing: string[] = [];
  if (!item.center?.trim()) missing.push('Center');
  return missing;
};

const validateScheduleFields = (item: ComplianceItemView): string[] => {
  const missing: string[] = [];
  if (!item.frequencyRequired?.trim()) missing.push('Frequency Required');
  return missing;
};

// Navigates to the newly-created item's edit page once a save produces an id for a brand-new
// compliance item. Extracted so handleSave doesn't branch on this itself.
const navigateToNewlySavedItem = (wasNewComplianceItem: boolean, newId: string | undefined, navigate: NavigateFunction) => {
  if (wasNewComplianceItem && newId) {
    setTimeout(() => navigate(`/compliance/edit?id=${newId}`, { replace: true }), 1500);
  }
};

const SaveComplianceItemButton: React.FC<SaveComplianceItemButtonProps> = ({
  label = 'Save Compliance Item',
  successMessage = 'Compliance item saved successfully!',
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [showValidationError, setShowValidationError] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const { currentComplianceItem, isEdited } = useAppSelector(state => state.compliance);
  const [upsertComplianceItem] = useUpsertComplianceItemMutation();
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const account = useAccount(accounts[0] || {});

  const validateRequiredFields = (): boolean => {
    if (!currentComplianceItem) return false;

    const missingFields: string[] = [
      ...validateIdentificationFields(currentComplianceItem),
      ...validateFacilityFields(currentComplianceItem),
      ...validateScheduleFields(currentComplianceItem),
    ];

    if (missingFields.length > 0) {
      setValidationMessage(`Please complete the following required fields: ${missingFields.join(', ')}`);
      setShowValidationError(true);
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!currentComplianceItem || !isEdited) return;

    if (!validateRequiredFields()) return;

    setIsSaving(true);
    try {
      const userName = account?.name || 'Unknown User';
      const wasNewComplianceItem = !currentComplianceItem.id;

      const result = await upsertComplianceItem({
        complianceItemView: currentComplianceItem,
        modifiedBy: userName,
      }).unwrap();

      setShowSuccess(true);
      navigateToNewlySavedItem(wasNewComplianceItem, result.id, navigate);
    } catch (error) {
      console.error('Failed to save compliance item:', error);
      setValidationMessage('Failed to save compliance item. Please try again.');
      setShowValidationError(true);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        onClick={handleSave}
        disabled={!isEdited || isSaving}
        startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Save />}
        sx={{
          backgroundColor: '#162346',
          color: 'white',
          fontWeight: 600,
          '&:hover': {
            backgroundColor: '#1a2a52',
          },
          '&:disabled': {
            backgroundColor: '#64748b',
            color: '#ffffff',
          },
        }}
      >
        {isSaving ? 'Saving...' : label}
      </Button>

      <Snackbar
        open={showValidationError}
        autoHideDuration={6000}
        onClose={() => setShowValidationError(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowValidationError(false)} severity="error" sx={{ width: '100%' }}>
          {validationMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={showSuccess}
        autoHideDuration={4000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowSuccess(false)} severity="success" variant="filled">
          {successMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SaveComplianceItemButton;
